import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { authFetch } from "../../App";
import { Building2, Users, ClipboardCheck, AlertTriangle, ArrowRight, FileText } from "lucide-react";

const API = "/api";

export default function HpsDashboard() {
  const [stats, setStats] = useState(null);
  const [objects, setObjects] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, o, i] = await Promise.all([
          authFetch(`${API}/hps/dashboard/stats`).then(r => r.json()),
          authFetch(`${API}/hps/objects`).then(r => r.json()),
          authFetch(`${API}/hps/inspections`).then(r => r.json()),
        ]);
        setStats(s);
        setObjects(Array.isArray(o) ? o : []);
        setInspections(Array.isArray(i) ? i : []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <div className="text-white/60 text-sm py-20 text-center" data-testid="hps-dashboard-loading">Daten werden geladen …</div>;
  }

  return (
    <div className="space-y-8" data-testid="hps-dashboard">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-white">Willkommen im HPS-Anlagencheck</h1>
        <p className="text-sm text-white/60 mt-1">Strukturierte Anlagenaufnahme · KI-Bewertung · Sanierungsvorschläge</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Kunden" value={stats?.customers ?? 0} color="#3b82f6" testid="hps-stat-customers" />
        <StatCard icon={Building2} label="Objekte" value={stats?.objects ?? 0} color="#6366f1" testid="hps-stat-objects" />
        <StatCard icon={ClipboardCheck} label="Prüfungen offen" value={stats?.inspections_open ?? 0} color="#22c55e" testid="hps-stat-inspections" />
        <StatCard icon={AlertTriangle} label="Mängel hoch/krit." value={stats?.defects?.hoch ?? 0} color="#f59e0b" testid="hps-stat-defects-high" />
      </div>

      {/* Letzte Prüfungen */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium text-white">Aktuelle Prüfungen</h2>
          <Link to="/app/hps-anlagencheck/objekte" className="text-xs text-blue-300 hover:text-blue-200 inline-flex items-center gap-1">
            Alle anzeigen <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="space-y-2">
          {inspections.length === 0 && (
            <div className="text-sm text-white/40">Noch keine Prüfungen vorhanden.</div>
          )}
          {inspections.slice(0, 5).map((ins) => {
            const obj = objects.find((o) => o.id === ins.object_id);
            return (
              <Link
                key={ins.id}
                to={`/app/hps-anlagencheck/pruefung/${ins.id}`}
                data-testid={`hps-inspection-row-${ins.id}`}
                className="block rounded-xl p-4 border border-white/10 hover:border-blue-400/40 hover:bg-white/5 transition-all"
                style={{ background: "rgba(255,255,255,0.03)" }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-sm font-medium text-white truncate">{ins.title}</span>
                      <StatusBadge status={ins.status} />
                      {ins.is_demo && <span className="text-[10px] uppercase tracking-wider text-amber-300 bg-amber-500/15 border border-amber-400/30 rounded px-1.5 py-0.5">Demo</span>}
                    </div>
                    <div className="text-xs text-white/55">{obj?.name || "—"} · {ins.inspection_date}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/40 shrink-0 mt-1" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Quick info */}
      <section className="rounded-xl border border-blue-400/20 p-5"
               style={{ background: "linear-gradient(135deg, rgba(74,158,255,0.08), rgba(37,99,235,0.04))" }}>
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-blue-300 mt-0.5 shrink-0" />
          <div className="text-sm text-white/80 leading-relaxed">
            <strong className="text-white">So funktioniert der HPS-Anlagencheck:</strong>
            <ol className="list-decimal pl-5 mt-2 space-y-1 text-white/70">
              <li>Strukturierte Aufnahme der Anlagenbereiche (Unterverteilungen, Lichtanlagen, Sicherheitsbeleuchtung, Kabelwege …)</li>
              <li>Mängel erfassen mit Priorität, Fotos und Messwerten</li>
              <li>KI erzeugt fachliche Zustandsbeschreibung, Risikoeinschätzung und Sanierungsvorschläge</li>
              <li>Fachliche Freigabe durch verantwortliche Elektrofachkraft / HPS</li>
              <li>Aus dem Bericht entsteht die Grundlage für Angebot oder Wartungsauftrag</li>
            </ol>
          </div>
        </div>
      </section>
    </div>
  );
}


function StatusBadge({ status }) {
  const map = {
    entwurf:       { label: "Entwurf",       cls: "bg-white/10 text-white/70 border-white/15" },
    in_pruefung:   { label: "in Prüfung",    cls: "bg-amber-500/15 text-amber-200 border-amber-400/30" },
    freigegeben:   { label: "Freigegeben",   cls: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30" },
    abgelehnt:     { label: "Abgelehnt",     cls: "bg-red-500/15 text-red-200 border-red-400/30" },
  };
  const x = map[status] || map.entwurf;
  return (
    <span className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border ${x.cls}`}>{x.label}</span>
  );
}


function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="rounded-xl p-5 border border-white/10 backdrop-blur-sm"
         data-testid={testid}
         style={{ background: "rgba(255,255,255,0.04)" }}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-white/50 font-medium">{label}</div>
          <div className="text-3xl font-semibold text-white mt-1.5">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: color }}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  );
}
